import * as d3 from 'd3';

// Adaptive date formatting -- TradingView-style: show finer granularity
// close-up, coarser when zoomed out, rather than one fixed '%b %d' format
// regardless of range (today's behavior). Each tier's approximate rendered
// width (at the 11px axis font) is tracked alongside it so tick spacing can
// actually reserve enough room for it -- a flat px-per-tick assumption was
// fine on desktop but let "Nov 2025"/"Jan 2026"-width labels collide on a
// ~320px mobile chart width.
const FORMAT_TIERS = [
  { maxDays: 3, format: d3.timeFormat('%H:%M'), labelWidth: 42 },
  { maxDays: 60, format: d3.timeFormat('%b %d'), labelWidth: 52 },
  { maxDays: 730, format: d3.timeFormat('%b %Y'), labelWidth: 68 },
  { maxDays: Infinity, format: d3.timeFormat('%Y'), labelWidth: 34 },
];

function pickFormatTier(domain) {
  const [start, end] = domain;
  const spanDays = (end - start) / 86400000;
  return FORMAT_TIERS.find((t) => spanDays <= t.maxDays) || FORMAT_TIERS[FORMAT_TIERS.length - 1];
}

// Ticks() with a raw count picks "nice" values, but at coarse zoom levels
// that can still land multiple ticks inside the same formatted label (e.g.
// quarterly ticks all rendered as just "%Y") -- passing a real d3 time
// interval instead of a bare count keeps every tick on a boundary that
// actually changes what's printed, so labels never repeat *and* (via
// labelWidth + a minimum gap) never crowd into each other on narrow charts.
function pickTickInterval(domain, availableWidth, labelWidth) {
  const [start, end] = domain;
  const spanDays = (end - start) / 86400000;
  const minSlot = labelWidth + 24; // label width + breathing room between ticks
  const targetTicks = Math.max(2, Math.floor(availableWidth / minSlot));
  if (spanDays <= 3) return null; // let d3 pick a sensible sub-hour interval itself
  if (spanDays <= 60) return d3.timeDay.every(Math.max(1, Math.round(spanDays / targetTicks)));
  if (spanDays <= 730) return d3.timeMonth.every(Math.max(1, Math.round(spanDays / 30 / targetTicks)));
  return d3.timeYear.every(Math.max(1, Math.round(spanDays / 365 / targetTicks)));
}

export function drawXAxis({ group, xScale, height, availableWidth, theme, showGrid = true }) {
  const domain = xScale.domain();
  const tier = pickFormatTier(domain);
  const interval = pickTickInterval(domain, availableWidth, tier.labelWidth);
  const fallbackCount = Math.max(2, Math.floor(availableWidth / (tier.labelWidth + 24)));
  const axis = d3
    .axisBottom(xScale)
    .ticks(interval || fallbackCount)
    .tickFormat(tier.format)
    .tickSize(showGrid ? -height : 6)
    .tickPadding(10);

  group.call(axis);
  group.select('.domain').attr('stroke', theme.axis.line);
  group
    .selectAll('.tick line')
    .attr('stroke', theme.grid.color)
    .attr('stroke-dasharray', theme.grid.dasharray)
    .attr('opacity', theme.grid.opacity);
  group
    .selectAll('.tick text')
    .attr('fill', theme.axis.text)
    .attr('font-size', theme.axis.fontSize)
    .attr('font-family', theme.axis.fontFamily);
}

export function drawYAxis({ group, yScale, availableWidth, theme, formatter = d3.format('$,.2f'), tickCount = 6, showGrid = true }) {
  const axis = d3
    .axisRight(yScale)
    .ticks(tickCount)
    .tickFormat(formatter)
    .tickSize(showGrid ? -availableWidth : 6)
    .tickPadding(10);

  group.attr('transform', `translate(${availableWidth},0)`).call(axis);
  group.select('.domain').attr('stroke', theme.axis.line);
  group
    .selectAll('.tick line')
    .attr('stroke', theme.grid.color)
    .attr('stroke-dasharray', theme.grid.dasharray)
    .attr('opacity', theme.grid.opacity);
  group
    .selectAll('.tick text')
    .attr('fill', theme.axis.text)
    .attr('font-size', theme.axis.fontSize)
    .attr('font-family', theme.axis.fontFamily)
    .style('font-variant-numeric', 'tabular-nums');
}

